import State.Error
import State.Loading
import State.NotFound
import State.Search
import State.Success
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import app.softwork.bootstrapcompose.Alert
import app.softwork.bootstrapcompose.Breakpoint
import app.softwork.bootstrapcompose.CloseButton
import app.softwork.bootstrapcompose.Color
import app.softwork.bootstrapcompose.Column
import app.softwork.bootstrapcompose.Container
import app.softwork.bootstrapcompose.Navbar
import app.softwork.bootstrapcompose.NavbarCollapseBehavior
import app.softwork.bootstrapcompose.NavbarPlacement
import app.softwork.bootstrapcompose.Row
import app.softwork.bootstrapcompose.icons.ExclamationTriangle
import app.softwork.bootstrapcompose.icons.Translate
import app.softwork.routingcompose.HashRouter
import app.softwork.routingcompose.NavLink
import app.softwork.routingcompose.Router
import com.volkswagen.vocs.grip.Fault
import com.volkswagen.vocs.grip.vocstis.ArticleResponse
import com.volkswagen.vocs.grip.vocstis.TariffKind
import com.volkswagen.vocs.grip.vocstis.TariffRegion
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.engine.js.JsError
import io.ktor.client.plugins.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.http.HttpStatusCode.Companion.NotFound
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.jetbrains.compose.web.ExperimentalComposeWebSvgApi
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.maxLength
import org.jetbrains.compose.web.attributes.minLength
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.pattern
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.DateInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Footer
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.H6
import org.jetbrains.compose.web.dom.Hr
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextInput
import org.jetbrains.compose.web.renderComposable
import org.jetbrains.compose.web.svg.fill
import org.jetbrains.compose.web.svg.height
import org.jetbrains.compose.web.svg.width
import kotlin.time.Clock

val gripBaseUrl get() = js("gripBaseUrl").unsafeCast<String>()

val currentTranslation: Translation @Composable get() = TranslationCompositionLocal.current

val translationPrefix
    @Composable get() = when (currentTranslation) {
        Translation.DE -> "/de"
        Translation.EN -> "/en"
    }

internal val TranslationCompositionLocal: ProvidableCompositionLocal<Translation> =
    compositionLocalOf { error("Translation not defined, cannot provide through TranslationCompositionLocal.") }

@JsName("Intl.getCanonicalLocales")
private external fun getCanonicalLocales(locales: Array<String>): Array<String>

@JsName("Intl.Locale")
private external class Locale(locale: String) {
    val language: String
}

fun getUserLanguage(): Translation {
    val userLocales = window.navigator.languages + window.navigator.language
    val canonical = getCanonicalLocales(userLocales)

    for (locale in canonical) {
        val base = Locale(locale).language

        when (base) {
            "de" -> {
                return Translation.DE
            }

            "en" -> {
                return Translation.EN
            }
        }
    }

    return Translation.EN
}

@Composable
fun withTranslation(
    translation: Translation,
    content: @Composable () -> Unit
) {
    CompositionLocalProvider(TranslationCompositionLocal.provides(translation)) {
        content()
    }
}

@JsModule("./custom.scss")
private external val customScss: dynamic

@ExperimentalComposeWebSvgApi
fun main() {
    customScss
    val translation = getUserLanguage()

    if (document.location?.pathname == "404.html") {
        renderComposable(rootElementId = "root") {
            withTranslation(translation) {
                Content {
                    Text(currentTranslation.pageNotFound)
                }
            }
        }
    } else {
        val gripClient = HttpClient(Js) {
            defaultRequest {
                url(gripBaseUrl)
            }
            setupGrIPClient()
        }

        val clock = Clock.System
        val timeZone = TimeZone.currentSystemDefault()

        var state by mutableStateOf<State>(
            Search(
                UserInput(
                    "",
                    tariffRegion = TariffRegion.De,
                    tariffKind = TariffKind.Import,
                    validationDate = clock.todayIn(timeZone),
                )
            )
        )

        renderComposable(rootElementId = "root") {
            withTranslation(translation) {
                HashRouter("/") {
                    route("/en") {
                        withTranslation(Translation.EN) {
                            route("imprint") {
                                Content {
                                    Imprint()
                                }
                            }
                            route("termsofuse") {
                                Content {
                                    TermsOfUse()
                                }
                            }
                            route("dataprotection") {
                                Content {
                                    DataProtection()
                                }
                            }
                            noMatch {
                                Content {
                                    SearchView(
                                        gripClient = gripClient,
                                        state = state,
                                    ) {
                                        state = it
                                    }
                                }
                            }
                        }
                    }
                    route("/de") {
                        withTranslation(Translation.DE) {
                            route("imprint") {
                                Content {
                                    Imprint()
                                }
                            }
                            route("termsofuse") {
                                Content {
                                    TermsOfUse()
                                }
                            }
                            route("dataprotection") {
                                Content {
                                    DataProtection()
                                }
                            }
                            noMatch {
                                Content {
                                    SearchView(
                                        gripClient = gripClient,
                                        state = state,
                                    ) {
                                        state = it
                                    }
                                }
                            }
                        }
                    }
                    noMatch {
                        Content {
                            SearchView(
                                gripClient = gripClient,
                                state = state,
                            ) {
                                state = it
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun Imprint() {
    when (currentTranslation) {
        Translation.DE -> ImprintDetailsDe()
        Translation.EN -> ImprintDetailsEn()
    }
}

@Composable
fun TermsOfUse() {
    when (currentTranslation) {
        Translation.DE -> TermsOfUseDe()
        Translation.EN -> TermsOfUseEn()
    }
}

@Composable
fun DataProtection() {
    H1 {
        Text(currentTranslation.dataProtection)
    }
    when (currentTranslation) {
        Translation.DE -> DataProtectionDe()
        Translation.EN -> DataProtectionEn()
    }
}

@ExperimentalComposeWebSvgApi
@Composable
fun Content(content: @Composable () -> Unit) {
    Navbar(
        fluid = true,
        colorScheme = Color.Dark,
        placement = NavbarPlacement.StickyTop,
        backgroundColor = Color.Dark,
        collapseBehavior = NavbarCollapseBehavior.Never,
        toggler = false,
        brand = {
            NavLink(translationPrefix, {
                classes("navbar-brand")
            }) {
                Text("VOCS-TIS")
            }
        }
    ) {
        Div({
            classes("nav-item", "col-lg-auto")
        }) {
            val to = when (currentTranslation) {
                Translation.DE -> "/en"
                Translation.EN -> "/de"
            }
            val currentRoute = Router.current.currentPath.path.drop(3)

            NavLink(to + currentRoute) {
                Translate {
                    height(24)
                    width(24)
                    fill("white")
                }
            }
        }
    }
    Main {
        Container {
            content()
        }
    }
    Footer(attrs = {
        classes("footer", "mt-auto")
    }) {
        Container {
            Hr()
            Row {
                Column {
                    H4 {
                        Text(currentTranslation.help)
                    }

                    Text(currentTranslation.helpMessage)
                    P {
                        A("mailto:vocs-service@volkswagen.de?subject=VOCS-TIS") {
                            Text("vocs-service@volkswagen.de")
                        }
                    }
                }
                Column {
                    Row {
                        H4 {
                            Text(currentTranslation.legal)
                        }
                    }
                    Row {
                        Column {
                            NavLink("$translationPrefix/termsofuse") {
                                Text(currentTranslation.termsOfUse)
                            }
                        }
                        Column {
                            NavLink("$translationPrefix/imprint") {
                                Text(currentTranslation.imprint)
                            }
                        }
                        Column {
                            NavLink(to = "$translationPrefix/dataprotection") {
                                Text(currentTranslation.dataProtection)
                            }
                        }
                        Column {
                            A("licensenotes.html") {
                                Text(currentTranslation.license)
                            }
                        }
                    }
                }
            }
            P {
                Text("© Volkswagen AG")
            }
        }
    }
}

sealed interface State {
    val input: UserInput

    data class Search(override val input: UserInput) : State
    data class Loading(override val input: UserInput) : State
    data class Success(val data: ArticleResponse, override val input: UserInput) : State
    data class Error(val error: Throwable, override val input: UserInput) : State
    data class NotFound(override val input: UserInput) : State
}

data class UserInput(
    val article: String,
    val tariffRegion: TariffRegion,
    val tariffKind: TariffKind,
    val validationDate: LocalDate?,
)

fun State.copy(
    article: String = this.input.article,
    tariffRegion: TariffRegion = this.input.tariffRegion,
    tariffKind: TariffKind = this.input.tariffKind,
    validationDate: LocalDate? = this.input.validationDate,
): State = when (this) {
    is Error -> Error(
        error = error,
        input.copy(
            article = article,
            tariffRegion = tariffRegion,
            tariffKind = tariffKind,
            validationDate = validationDate
        )
    )

    is Loading -> Loading(
        input.copy(
            article = article,
            tariffRegion = tariffRegion,
            tariffKind = tariffKind,
            validationDate = validationDate
        )
    )

    is Search -> Search(
        input.copy(
            article = article,
            tariffRegion = tariffRegion,
            tariffKind = tariffKind,
            validationDate = validationDate
        )
    )

    is Success -> Success(
        data = data,
        input = input.copy(
            article = article,
            tariffRegion = tariffRegion,
            tariffKind = tariffKind,
            validationDate = validationDate
        )
    )

    is State.NotFound -> NotFound(
        input = input.copy(
            article = article,
            tariffRegion = tariffRegion,
            tariffKind = tariffKind,
            validationDate = validationDate
        )
    )
}

@OptIn(ExperimentalComposeWebSvgApi::class)
@Composable
fun OldWebsiteAlert() {
    var show by remember { mutableStateOf(true) }
    if (show) {
        Alert(
            color = Color.Danger
        ) {
            H4 {
                ExclamationTriangle {
                    classes("me-2")
                    height(24)
                    width(24)
                }
                Text(currentTranslation.redirect)
            }
            P {
                Text(currentTranslation.redirectMessage)
            }
            Hr {}
            P {
                Text(currentTranslation.redirectTodo)
            }
            CloseButton { show = false }
        }
    }
}

@Composable
fun SearchView(
    gripClient: HttpClient,
    state: State,
    changeState: (State) -> Unit,
) {
    OldWebsiteAlert()

    val scope = rememberCoroutineScope()

    Div {
        H1 {
            Text(currentTranslation.title)
        }
        Form(attrs = {
            id("form")
            onSubmit {
                it.preventDefault()

                changeState(Loading(state.input))

                scope.launch {
                    try {
                        val response = gripClient.get(
                            "tariff/classification/${state.input.article.lowercase()}",
                        ) {
                            parameter(
                                "tariffRegion",
                                TariffRegion.serializer().descriptor.getElementName(state.input.tariffRegion.ordinal)
                            )
                            parameter(
                                "tariffKind",
                                TariffKind.serializer().descriptor.getElementName(state.input.tariffKind.ordinal)
                            )
                            parameter("validationDate", state.input.validationDate.toString())

                            contentType(ContentType.Application.Json)
                        }

                        if (response.status == NotFound) {
                            changeState(NotFound(state.input))
                        } else if (response.status.isSuccess()) {
                            changeState(Success(response.body(), state.input))
                        } else {
                            val output = response.body<Fault>()
                            changeState(Error(output, state.input))
                        }
                    } catch (e: Throwable) {
                        changeState(Error(e, state.input))
                    }
                }
            }
        }) {
            Row {
                Column(breakpoint = Breakpoint.Small) {
                    Div({ classes("form-floating", "mb-3") }) {

                        val placeHolder = currentTranslation.articlePlaceholder
                        TextInput(state.input.article.uppercase()) {
                            classes("form-control")
                            id("articleNumber")

                            placeholder(placeHolder)
                            onInput {
                                changeState(state.copy(article = it.value.uppercase()))
                            }
                            attr("aria-describedby", "articleNumberHelpBlock")
                            pattern("\\w{5,15}")
                            minLength(5)
                            maxLength(15)
                            required()
                        }
                        Label(forId = "articleNumber") {
                            Text(currentTranslation.articleLabel)
                        }
                        Div({
                            classes("form-text")
                            id("articleNumberHelpBlock")
                        }) {
                            Text(currentTranslation.articleHelp)
                        }
                    }
                }
            }
            Row {
                Column(breakpoint = Breakpoint.Small) {
                    Div({ classes("form-floating", "mb-3") }) {
                        Select({
                            classes("form-select")
                            id("tariffRegion")
                            onInput {
                                changeState(state.copy(tariffRegion = TariffRegion.valueOf(it.value!!)))
                            }
                        }) {
                            for (tariffRegion in TariffRegion.entries) {
                                Option(tariffRegion.name, {
                                    if (tariffRegion == state.input.tariffRegion) {
                                        selected()
                                    }
                                }) {
                                    val text = when (tariffRegion) {
                                        TariffRegion.Be -> "\uD83C\uDDE7\uD83C\uDDEA BE"
                                        TariffRegion.Br -> "\uD83C\uDDE7\uD83C\uDDF7 BR"
                                        TariffRegion.De -> "\uD83C\uDDE9\uD83C\uDDEA DE"
                                        TariffRegion.Es -> "\uD83C\uDDEA\uD83C\uDDF8 ES"
                                        TariffRegion.Gb -> "\uD83C\uDDEC\uD83C\uDDE7 GB"
                                        TariffRegion.Hu -> "\uD83C\uDDED\uD83C\uDDFA HU"
                                        TariffRegion.My -> "\uD83C\uDDF2\uD83C\uDDFE MY"
                                        TariffRegion.Pl -> "\uD83C\uDDF5\uD83C\uDDF1 PL"
                                        TariffRegion.Pt -> "\uD83C\uDDF5\uD83C\uDDF9 PT"
                                        TariffRegion.Sk -> "\uD83C\uDDF8\uD83C\uDDF0 SK"
                                    }
                                    Text(text)
                                }
                            }
                        }
                        Label(forId = "tariffRegion") {
                            Text(currentTranslation.tariffRegion)
                        }
                    }
                }
                Column(breakpoint = Breakpoint.Small) {
                    Div({ classes("form-floating", "mb-3") }) {
                        Select({
                            classes("form-select")
                            id("tariffKind")

                            onInput {
                                changeState(state.copy(tariffKind = TariffKind.valueOf(it.value!!)))
                            }
                        }) {
                            for (tariffKind in TariffKind.entries) {
                                Option(tariffKind.name, {
                                    if (tariffKind == state.input.tariffKind) {
                                        selected()
                                    }
                                }) {
                                    Text(tariffKind.name.uppercase())
                                }
                            }
                        }
                        Label(forId = "tariffKind") {
                            Text(currentTranslation.tariffKind)
                        }
                    }
                }
                Column(breakpoint = Breakpoint.Small) {
                    Div({ classes("form-floating", "mb-3") }) {
                        DateInput(state.input.validationDate?.toString() ?: "") {
                            classes("form-control")
                            id("validationDate")

                            onInput {
                                changeState(state.copy(validationDate = LocalDate.Formats.ISO.parseOrNull(it.value)))
                            }
                        }
                        Label(forId = "validationDate") {
                            Text(currentTranslation.validationDate)
                        }
                    }
                }
            }
            Row {
                Column {
                    Button(attrs = {
                        classes("btn", "btn-primary")
                        id("submit")
                        type(ButtonType.Submit)

                        if (!state.input.article.matches(Regex("\\w{5,15}")) || state is Loading) {
                            disabled()
                        }
                    }) {
                        if (state is Loading) {
                            Span({
                                classes("spinner-border", "spinner-border-sm")
                            })
                            Span(attrs = {
                                attr("role", "status")
                            }) {
                                Text(currentTranslation.loading)
                            }
                        } else {
                            Text(currentTranslation.submit)
                        }
                    }
                }
            }
        }
    }

    when (val stateValue = state) {
        is Success -> {
            Row {
                H6 {
                    Text(currentTranslation.explanation)
                }

                Column {
                    H4 { Text(currentTranslation.alias) }
                    Text(stateValue.data.articleAlias ?: "")
                }
                Column {
                    H4 {
                        Text(currentTranslation.descriptions)
                    }
                    P {
                        Text(stateValue.data.description ?: "")
                    }
                    P {
                        Text(stateValue.data.description2 ?: "")
                    }
                    for (translation in stateValue.data.translations) {
                        val flag = when (translation.key) {
                            "de-DE" -> "\uD83C\uDDE9\uD83C\uDDEA"
                            "en-GB" -> "\uD83C\uDDEC\uD83C\uDDE7"
                            "fr-FR" -> "\uD83C\uDDEB\uD83C\uDDF7"
                            "es-ES" -> "\uD83C\uDDEA\uD83C\uDDF8"
                            "it-IT" -> "\uD83C\uDDEE\uD83C\uDDF9"
                            "pt-PT" -> "\uD83C\uDDF5\uD83C\uDDF9"
                            "nl-NL" -> "\uD83C\uDDF3\uD83C\uDDF1"
                            "da-DK" -> "\uD83C\uDDE9\uD83C\uDDF0"
                            "sv-SE" -> "\uD83C\uDDF8\uD83C\uDDEA"
                            "nb-NO" -> "\uD83C\uDDF3\uD83C\uDDF4"
                            "fi-FI" -> "\uD83C\uDDEB\uD83C\uDDEE"
                            "sr-BA" -> "\uD83C\uDDF7\uD83C\uDDF8"
                            "el-GR" -> "\uD83C\uDDEC\uD83C\uDDF7"
                            "cs-CZ" -> "\uD83C\uDDE8\uD83C\uDDFF"
                            "sk-SK" -> "\uD83C\uDDF8\uD83C\uDDF0"
                            "hu-HU" -> "\uD83C\uDDED\uD83C\uDDFA"
                            "pl-PL" -> "\uD83C\uDDF5\uD83C\uDDF1"
                            "zh-CN" -> "\uD83C\uDDE8\uD83C\uDDF3"
                            "ja-JP" -> "\uD83C\uDDEF\uD83C\uDDF5"
                            "sl-SI" -> "\uD83C\uDDF8\uD83C\uDDEE"
                            "en-US" -> "\uD83C\uDDFA\uD83C\uDDF8"
                            "ro-RO" -> "\uD83C\uDDF7\uD83C\uDDF4"
                            "hr-HR" -> "\uD83C\uDDED\uD83C\uDDF7"
                            "tr-TR" -> "\uD83C\uDDF9\uD83C\uDDF7"
                            "pt-BR" -> "\uD83C\uDDE7\uD83C\uDDF7"
                            else -> null
                        }
                        if (flag != null) {
                            P {
                                Span {
                                    Text(flag)
                                    Text(translation.value)
                                }
                            }
                        }
                    }
                }
                Column {
                    H4 {
                        Text(currentTranslation.hsCode)
                    }
                    Text(stateValue.data.tariffNumber)
                }
                Column {
                    H4 {
                        Text(currentTranslation.validFrom)
                    }
                    Text(stateValue.data.validFrom?.toString() ?: "")
                }
                Column {
                    H4 {
                        Text(currentTranslation.validUntil)
                    }
                    Text(stateValue.data.validTo?.toString() ?: "")
                }
            }
        }

        is Error -> {
            Text(currentTranslation.error)
        }

        is State.NotFound -> {
            Text(currentTranslation.notFound)
        }

        is Loading -> {}
        is Search -> {}
    }
}
